import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Check, Star, Zap, Loader2, Tag, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

const ENT_LABELS = {
  featured: "Featured placement", priority_gigs: "Priority gig visibility", vip_badge: "VIP badge",
  recommended_badge: "Recommended badge", homepage_eligible: "Homepage eligibility", directory_priority: "Directory priority",
  gig_recommendation_priority: "Gig recommendation priority", sponsor_section: "Sponsor section", merch_section: "Merchandise",
  video_section: "Videos", discography_section: "Discography", booking_calendar: "Booking calendar",
  advanced_analytics: "Advanced analytics", deals_posting: "Post Deals", hiring_posting: "Post Hiring Hub jobs",
  gig_posting: "Post Gigs", booking_system: "Booking system", private_event_requests: "Private event requests",
  menu_uploads: "Menu uploads", vip_spotlight: "VIP spotlight", homepage_spotlight: "Homepage spotlight",
  countdown: "Countdown", rsvp: "RSVP", social_promo: "Social media promotion",
  rides_section: "Rides & attractions section", games_section: "Games & arcade section", ticket_sales: "Ticket options & sales links",
  event_posting: "Post events", internal_booking: "Booking requests", recurring_schedules: "Recurring schedules",
};
export const entLabel = (k) => ENT_LABELS[k] || k.replace(/_/g, " ");

const FAQS = [
  ["Can I cancel anytime?", "Yes. Cancel from your dashboard — your plan stays active until the end of the paid period and simply won't renew."],
  ["What happens to my listing on the free plan?", "Free listings keep every profile feature. Paid plans add visibility: featured placement, badges, and priority."],
  ["Do launch prices change?", "Launch pricing is limited-time. Once regular pricing kicks in, existing subscribers keep renewing at the price they signed up with while their plan stays active."],
  ["How do event boosts work?", "Boosts are one-time payments. Your event gets featured placement for the boost window, then automatically returns to normal."],
  ["Do you offer discounts?", "Yes — promo codes can be applied at checkout when available."],
];

export function CheckoutDialog({ plan, interval = "monthly", onClose }) {
  const { user } = useAuth();
  const yearly = interval === "yearly" && plan.plan_type === "subscription" && Number(plan.yearly_price) > 0;
  const basePrice = yearly ? plan.yearly_price : plan.price;
  const nav = useNavigate();
  const [entities, setEntities] = useState(null);
  const [entityId, setEntityId] = useState("");
  const [code, setCode] = useState("");
  const [disc, setDisc] = useState(null);
  const [busy, setBusy] = useState(false);
  const coll = plan.target === "artist" ? "artists" : plan.target === "venue" ? "venues" : "events";

  useEffect(() => {
    if (!user) return;
    api.get(`/my/${coll}`).then((r) => { setEntities(r.data); if (r.data.length === 1) setEntityId(r.data[0].id); }).catch(() => setEntities([]));
  }, [user, coll]);

  const applyCode = async () => {
    try {
      const { data } = await api.post("/checkout/validate-discount", { code, plan_id: plan.id });
      setDisc(data); toast.success(`Code applied: $${data.discounted_price}`);
    } catch (e) { setDisc(null); toast.error(errMsg(e)); }
  };

  const checkout = async () => {
    if (!entityId) { toast.error(`Select which ${plan.target} this plan is for`); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/checkout/session", { plan_id: plan.id, entity_id: entityId, interval: yearly ? "yearly" : "monthly", discount_code: disc ? disc.code : "", origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  if (!user) {
    return (
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="bg-[#111] border-white/10 text-white max-w-sm">
          <DialogHeader><DialogTitle className="font-display">Log in to continue</DialogTitle>
            <DialogDescription className="text-zinc-400">You need an account to purchase {plan.name}.</DialogDescription></DialogHeader>
          <button onClick={() => nav("/login")} className="bg-rose-500 hover:bg-rose-600 font-bold px-6 py-2.5 rounded-lg" data-testid="checkout-login-btn">Log in / Join free</button>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#111] border-white/10 text-white max-w-md" data-testid="checkout-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{plan.name}</DialogTitle>
          <DialogDescription className="text-zinc-400">${disc && !yearly ? disc.discounted_price : basePrice}{yearly ? "/year" : plan.interval === "monthly" ? "/month" : plan.interval === "yearly" ? "/year" : " one-time"}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <p className="text-xs text-zinc-400 mb-1.5">Apply to which {plan.target}?</p>
            {entities === null ? <p className="text-sm text-zinc-500">Loading…</p> : entities.length === 0 ? (
              <p className="text-sm text-amber-300">You don't own any {plan.target} listings yet. <Link to="/submit" className="underline">Submit one first</Link>.</p>
            ) : (
              <select className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2.5 text-sm" value={entityId} onChange={(e) => setEntityId(e.target.value)} data-testid="checkout-entity">
                <option value="">Select…</option>
                {entities.map((x) => <option key={x.id} value={x.id}>{x.name || x.title}</option>)}
              </select>
            )}
          </div>
          <div className="flex gap-2">
            <input className="flex-1 bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2.5 text-sm" placeholder="Promo code (optional)" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} data-testid="checkout-promo" />
            <button onClick={applyCode} disabled={!code} className="border border-white/15 px-4 rounded-lg text-sm disabled:opacity-40" data-testid="checkout-apply-promo"><Tag size={14} className="inline mr-1" />Apply</button>
          </div>
          {disc && <p className="text-xs text-emerald-300">Code {disc.code}: ${disc.original_price} → ${disc.discounted_price}</p>}
          <button onClick={checkout} disabled={busy || !entityId} className="w-full bg-rose-500 hover:bg-rose-600 font-bold py-3 rounded-lg disabled:opacity-40 flex items-center justify-center gap-2" data-testid="checkout-pay-btn">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />} Continue to secure checkout
          </button>
          <p className="text-[11px] text-zinc-600 text-center">Payments securely processed by Stripe.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PlanCard({ p, onBuy, highlight, billing = "monthly" }) {
  const launch = p.launch_active && p.launch_price !== p.regular_price && Number(p.regular_price) > Number(p.launch_price);
  const yearly = billing === "yearly" && p.plan_type === "subscription" && Number(p.yearly_price) > 0;
  const shownPrice = yearly ? p.yearly_price : p.price;
  const saving = yearly && Number(p.price) > 0 ? Math.max(0, Math.round(Number(p.price) * 12 - Number(p.yearly_price))) : 0;
  return (
    <div className={`rounded-2xl border p-6 flex flex-col ${highlight ? "border-rose-500 bg-rose-500/5" : "border-white/10 bg-[#111]"}`} data-testid={`plan-card-${p.key || p.id}`}>
      {p.badge && <span className="self-start text-[10px] font-extrabold uppercase tracking-wider bg-amber-400 text-black px-2 py-0.5 rounded-full mb-3">{p.badge}</span>}
      <h3 className="font-display font-bold text-xl">{p.name}</h3>
      <div className="mt-2 flex items-end gap-2">
        <span className="font-display font-extrabold text-4xl">${shownPrice}</span>
        <span className="text-zinc-500 text-sm mb-1">{yearly ? "/yr" : p.interval === "monthly" ? "/mo" : p.interval === "yearly" ? "/yr" : "one-time"}</span>
        {!yearly && launch && <span className="text-sm text-zinc-500 line-through mb-1">${p.regular_price}</span>}
      </div>
      {yearly && saving > 0 && <p className="text-xs text-emerald-300 mt-1">Save ${saving} vs monthly</p>}
      {billing === "yearly" && !yearly && p.plan_type === "subscription" && Number(p.price) > 0 && <p className="text-xs text-zinc-500 mt-1">Monthly billing only</p>}
      {!yearly && launch && <p className="text-xs text-emerald-300 mt-1">Launch price — regular ${p.regular_price}/mo</p>}
      <p className="text-sm text-zinc-400 mt-3">{p.description}</p>
      <ul className="mt-4 space-y-2 flex-1" data-testid={`plan-features-${p.key || p.id}`}>
        {p.features_intro && <li className="text-sm font-bold text-rose-300">{p.features_intro}</li>}
        {(p.features && p.features.length ? p.features : (p.entitlements || []).map(entLabel)).map((f) => (
          <li key={f} className="text-sm flex items-start gap-2"><Check size={15} className="text-emerald-400 shrink-0 mt-0.5" /> {f}</li>
        ))}
      </ul>
      {p.status === "coming_soon" ? (
        <span className="mt-5 text-center text-sm text-zinc-500 border border-white/10 rounded-full py-2.5">Coming soon</span>
      ) : Number(p.price) === 0 ? (
        <Link to="/submit" className="mt-5 text-center font-bold border border-white/15 hover:border-white/40 rounded-full py-2.5 text-sm" data-testid={`plan-cta-${p.key || p.id}`}>Get started free</Link>
      ) : (
        <button onClick={() => onBuy(p, yearly ? "yearly" : "monthly")} className={`mt-5 font-bold rounded-full py-2.5 text-sm ${highlight ? "bg-rose-500 hover:bg-rose-600" : "border border-rose-500/50 text-rose-300 hover:bg-rose-500/10"}`} data-testid={`plan-cta-${p.key || p.id}`}>
          {p.plan_type === "boost" ? "Buy boost" : yearly ? "Subscribe yearly" : "Subscribe"}
        </button>
      )}
    </div>
  );
}

export function ComparisonTable({ items, billing, onBuy, label }) {
  if (items.length < 2) return null;
  const free = items.find((p) => Number(p.price) === 0);
  const rows = [];
  const seen = new Set();
  const add = (f) => { if (f && !seen.has(f)) { seen.add(f); rows.push(f); } };
  (free?.features || []).forEach(add);
  items.forEach((p) => (p.features || []).forEach(add));
  if (rows.length === 0) return null;
  const has = (p, f) => (p.features || []).includes(f) || (!!p.features_intro && (free?.features || []).includes(f));
  const key = label.toLowerCase().replace(/[^a-z]+/g, "-");
  return (
    <details className="mt-8 group" data-testid={`plan-compare-${key}`}>
      <summary className="cursor-pointer list-none inline-flex items-center gap-2 text-sm font-bold text-rose-300 hover:text-rose-200 border border-rose-500/30 rounded-full px-5 py-2.5" data-testid={`plan-compare-toggle-${key}`}>
        Compare {label.toLowerCase()} side-by-side <span className="group-open:rotate-180 transition-transform"><ChevronDown size={15} /></span>
      </summary>
      <div className="mt-5 overflow-x-auto rounded-2xl border border-white/10">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="bg-[#161616]">
              <th className="text-left font-bold px-4 py-3.5 text-zinc-400 w-[40%]">Features</th>
              {items.map((p) => {
                const vip = (p.badge || "").toLowerCase().includes("vip");
                const yearly = billing === "yearly" && p.plan_type === "subscription" && Number(p.yearly_price) > 0;
                return (
                  <th key={p.id} className={`px-4 py-3.5 text-center ${vip ? "bg-rose-500/10" : ""}`}>
                    <p className="font-display font-bold">{p.name}</p>
                    <p className="text-xs text-zinc-400 font-normal mt-0.5">
                      ${yearly ? p.yearly_price : p.price}{Number(p.price) === 0 ? "" : yearly ? "/yr" : p.interval === "monthly" ? "/mo" : p.interval === "yearly" ? "/yr" : " one-time"}
                    </p>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((f, i) => (
              <tr key={f} className={i % 2 ? "bg-white/[0.02]" : ""}>
                <td className="px-4 py-2.5 text-zinc-300">{f}</td>
                {items.map((p) => {
                  const vip = (p.badge || "").toLowerCase().includes("vip");
                  return (
                    <td key={p.id} className={`px-4 py-2.5 text-center ${vip ? "bg-rose-500/[0.06]" : ""}`}>
                      {has(p, f) ? <Check size={16} className="text-emerald-400 inline" aria-label="Included" /> : <span className="text-zinc-700">—</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr className="bg-[#161616]">
              <td className="px-4 py-3.5" />
              {items.map((p) => {
                const vip = (p.badge || "").toLowerCase().includes("vip");
                const yearly = billing === "yearly" && p.plan_type === "subscription" && Number(p.yearly_price) > 0;
                return (
                  <td key={p.id} className={`px-4 py-3.5 text-center ${vip ? "bg-rose-500/10" : ""}`}>
                    {p.status === "coming_soon" ? <span className="text-xs text-zinc-500">Coming soon</span> : Number(p.price) === 0 ? (
                      <Link to="/submit" className="inline-block text-xs font-bold border border-white/20 hover:border-white/50 rounded-full px-4 py-2" data-testid={`compare-cta-${p.key || p.id}`}>Get started</Link>
                    ) : (
                      <button onClick={() => onBuy(p, yearly ? "yearly" : "monthly")} data-testid={`compare-cta-${p.key || p.id}`}
                        className={`text-xs font-bold rounded-full px-4 py-2 ${vip ? "bg-rose-500 hover:bg-rose-600 text-white" : "border border-rose-500/50 text-rose-300 hover:bg-rose-500/10"}`}>
                        {p.plan_type === "boost" ? "Buy boost" : "Subscribe"}
                      </button>
                    )}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </details>
  );
}

export default function Pricing() {
  const { target } = useParams();
  const t = (target || "").replace(/s$/, "").replace("activitie", "activity");
  const [plans, setPlans] = useState([]);
  const [buy, setBuy] = useState(null);
  const [billing, setBilling] = useState("monthly");
  useEffect(() => { api.get(`/plans${t ? `?target=${t}` : ""}`).then((r) => setPlans(r.data)).catch(() => {}); }, [t]);

  const groups = { artist: [], venue: [], event: [], attraction: [], activity: [] };
  plans.forEach((p) => groups[p.target]?.push(p));
  const tabs = [["", "All plans"], ["artists", "Artists"], ["venues", "Venues"], ["attractions", "Attractions"], ["activities", "Activities"], ["events", "Event Boosts"]];
  const hasYearly = plans.some((p) => Number(p.yearly_price) > 0);

  const Section = ({ label, items }) => items.length > 0 && (
    <div className="mb-14">
      <h2 className="font-display font-bold text-2xl mb-6 flex items-center gap-2"><Star size={20} className="text-rose-400" /> {label}</h2>
      <div className={`grid gap-5 sm:grid-cols-2 ${items.length > 3 ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
        {items.map((p) => <PlanCard key={p.id} p={p} billing={billing} onBuy={(plan, interval) => setBuy({ plan, interval })} highlight={(p.badge || "").toLowerCase().includes("vip")} />)}
      </div>
      <ComparisonTable items={items} billing={billing} label={label} onBuy={(plan, interval) => setBuy({ plan, interval })} />
    </div>
  );

  return (
    <div className="px-6 md:px-12 lg:px-20 py-14" data-testid="pricing-page">
      <p className="overline">Gulf Coast VIP</p>
      <h1 className="font-display font-extrabold text-4xl sm:text-5xl">Plans & Boosts</h1>
      <p className="text-zinc-400 mt-3 max-w-2xl">Get seen first. Featured placement, VIP badges, priority gig visibility and event boosts — all cancel-anytime.</p>
      <div className="flex gap-2 mt-8 mb-4 flex-wrap">
        {tabs.map(([k, l]) => (
          <Link key={k} to={`/pricing${k ? `/${k}` : ""}`} data-testid={`pricing-tab-${k || "all"}`}
            className={`px-4 py-2 rounded-full text-sm font-medium border ${(target || "") === k ? "bg-rose-500 border-rose-500 text-white" : "border-white/15 text-zinc-400 hover:text-white"}`}>{l}</Link>
        ))}
      </div>
      {hasYearly && (
        <div className="inline-flex items-center gap-1 bg-[#111] border border-white/10 rounded-full p-1 mb-10" data-testid="billing-toggle">
          {[["monthly", "Monthly"], ["yearly", "Yearly"]].map(([k, l]) => (
            <button key={k} onClick={() => setBilling(k)} className={`px-4 py-1.5 rounded-full text-sm font-bold ${billing === k ? "bg-rose-500" : "text-zinc-400 hover:text-white"}`} data-testid={`billing-${k}`}>
              {l}{k === "yearly" && <span className="ml-1.5 text-[10px] uppercase text-emerald-300 font-extrabold">Save</span>}
            </button>
          ))}
        </div>
      )}
      {(!t || t === "artist") && <Section label="Artist plans" items={groups.artist} />}
      {(!t || t === "venue") && <Section label="Venue plans" items={groups.venue} />}
      {(!t || t === "attraction") && <Section label="Attraction plans" items={groups.attraction} />}
      {(!t || t === "activity") && <Section label="Activity plans" items={groups.activity} />}
      {(!t || t === "event") && <Section label="Event boosts" items={groups.event} />}
      <div className="max-w-3xl mt-6">
        <h2 className="font-display font-bold text-2xl mb-5">FAQ</h2>
        <div className="space-y-3">
          {FAQS.map(([q, a]) => (
            <details key={q} className="bg-[#111] border border-white/10 rounded-xl px-5 py-4 group">
              <summary className="font-medium cursor-pointer list-none flex justify-between items-center">{q}<span className="text-zinc-500 group-open:rotate-45 transition-transform">+</span></summary>
              <p className="text-sm text-zinc-400 mt-2">{a}</p>
            </details>
          ))}
        </div>
      </div>
      {buy && <CheckoutDialog plan={buy.plan} interval={buy.interval} onClose={() => setBuy(null)} />}
    </div>
  );
}
